/**
 * @fileoverview Terms of Service page for Onicorn platform
 * @module routes/terms
 */

import type { ReactElement } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';

/**
 * Terms of Service page component.
 *
 * Displays the complete Terms of Service for the Onicorn platform,
 * covering eligibility, platform nature, non-custodial operations,
 * KOL relationships, and legal disclaimers.
 *
 * @component
 */
function TermsPage(): ReactElement {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-6 pt-28 pb-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#B3B3B3] hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-3xl md:text-4xl font-bold mb-2">Terms of Service</h1>
        <p className="text-[#B3B3B3] mb-8">Last updated: January 2026</p>

        {/* Content */}
        <div className="prose prose-invert max-w-none space-y-8">
          {/* Section 1 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              By accessing or using the Onicorn platform ("Platform"), you agree to be bound by these Terms of Service ("Terms").
              If you do not agree to these Terms, you may not access or use the Platform. These Terms constitute a legally
              binding agreement between you and Onicorn.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">2. Eligibility</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              To use the Platform, you must be at least 18 years old or the age of majority in your jurisdiction, whichever
              is higher. By using the Platform, you represent and warrant that you meet these eligibility requirements.
              You must also have the legal capacity to enter into binding contracts in your jurisdiction.
            </p>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              Users from jurisdictions where cryptocurrency activities are prohibited or restricted may not use the Platform.
              It is your responsibility to ensure compliance with local laws and regulations.
            </p>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">3. Platform Nature and Purpose</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              Onicorn is a Web3 fundraising platform that connects blockchain projects, Key Opinion Leaders (KOLs),
              and investors through transparent, on-chain fundraising mechanisms. The Platform facilitates:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Project fundraising through smart contracts</li>
              <li>KOL-mediated deal distribution and promotion</li>
              <li>Investor participation in verified fundraising rounds</li>
              <li>Transparent tracking of investments and vesting schedules</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">4. Non-Custodial Operations</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              Onicorn operates on a non-custodial basis. This means:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>We do not hold, manage, or have access to your cryptocurrency assets</li>
              <li>All transactions occur directly on the blockchain through smart contracts</li>
              <li>You are solely responsible for maintaining the security of your wallet and private keys</li>
              <li>Lost private keys or wallet access cannot be recovered by Onicorn</li>
            </ul>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              By using our Platform, you acknowledge that you understand and accept the inherent risks of non-custodial
              blockchain operations.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">5. Key Opinion Leaders (KOLs) and Commissions</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              KOLs play a significant role in the Onicorn ecosystem by curating and promoting deals to their communities.
              KOLs may receive commissions based on investments made through their referral links. These commissions are:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Transparently disclosed on the Platform</li>
              <li>Automatically distributed via smart contracts</li>
              <li>Subject to the terms agreed upon between KOLs and projects</li>
            </ul>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              Onicorn does not guarantee the accuracy of information provided by KOLs. Investors should conduct their own
              research before making investment decisions.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">6. User Accounts and Wallet Connection</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              To participate in fundraising activities, you must connect a compatible cryptocurrency wallet to the Platform.
              You are responsible for:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Maintaining the security and confidentiality of your wallet credentials</li>
              <li>All activities that occur through your connected wallet</li>
              <li>Ensuring you have sufficient funds and network fees for transactions</li>
              <li>Verifying transaction details before confirmation</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">7. Investment Disclaimers</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              <strong className="text-white">IMPORTANT:</strong> Cryptocurrency investments carry significant risks.
              By using the Platform, you acknowledge and agree that:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Past performance does not guarantee future results</li>
              <li>You may lose some or all of your invested capital</li>
              <li>Cryptocurrency markets are highly volatile and unpredictable</li>
              <li>Projects may fail to deliver on their promises or roadmaps</li>
              <li>Token values may decline significantly or become worthless</li>
              <li>Vesting schedules may result in locked tokens during price declines</li>
            </ul>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              Onicorn does not provide investment advice. All information on the Platform is for informational purposes
              only and should not be construed as financial, legal, or tax advice.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">8. Prohibited Activities</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              You agree not to engage in any of the following prohibited activities:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Using the Platform for money laundering, terrorist financing, or other illegal activities</li>
              <li>Attempting to manipulate or interfere with Platform operations or smart contracts</li>
              <li>Creating multiple accounts to circumvent Platform rules or limits</li>
              <li>Impersonating other users, KOLs, or projects</li>
              <li>Spreading false or misleading information about projects or investments</li>
              <li>Exploiting bugs, vulnerabilities, or errors in the Platform or smart contracts</li>
              <li>Using automated systems, bots, or scripts without authorization</li>
              <li>Violating any applicable laws, regulations, or third-party rights</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">9. Privacy and Data Protection</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              Your use of the Platform is also governed by our{' '}
              <Link to="/privacy" className="text-purple-400 hover:text-purple-300 underline">
                Privacy Policy
              </Link>
              , which describes how we collect, use, and protect your information. By using the Platform,
              you consent to our data practices as described in the Privacy Policy.
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">10. Regulatory Compliance</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              You are solely responsible for ensuring that your use of the Platform complies with all applicable laws
              and regulations in your jurisdiction, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Securities laws and regulations</li>
              <li>Anti-money laundering (AML) requirements</li>
              <li>Know Your Customer (KYC) obligations</li>
              <li>Tax reporting and payment obligations</li>
              <li>Foreign exchange controls</li>
            </ul>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              Onicorn may implement KYC/AML procedures as required by law or at our discretion.
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">11. Intellectual Property</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              All content, trademarks, logos, and intellectual property on the Platform are owned by or licensed to Onicorn.
              You may not copy, modify, distribute, or create derivative works from any Platform content without our
              express written permission.
            </p>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              Project information, logos, and content displayed on the Platform remain the property of their respective owners.
            </p>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">12. Risk Disclosure</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              By using the Platform, you acknowledge the following risks:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li><strong className="text-white">Smart Contract Risk:</strong> Smart contracts may contain bugs or vulnerabilities that could result in loss of funds</li>
              <li><strong className="text-white">Regulatory Risk:</strong> Changes in laws or regulations could impact the Platform or your investments</li>
              <li><strong className="text-white">Market Risk:</strong> Cryptocurrency markets are highly volatile and subject to rapid price changes</li>
              <li><strong className="text-white">Project Risk:</strong> Projects may fail, be fraudulent, or not deliver promised results</li>
              <li><strong className="text-white">Technology Risk:</strong> Blockchain networks may experience congestion, forks, or other issues</li>
              <li><strong className="text-white">Liquidity Risk:</strong> Tokens may have limited or no liquidity on exchanges</li>
            </ul>
          </section>

          {/* Section 13 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">13. Limitation of Liability</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, ONICORN AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES,
              AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
              INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Loss of profits, revenue, or data</li>
              <li>Loss of cryptocurrency or digital assets</li>
              <li>Damages arising from smart contract failures or exploits</li>
              <li>Damages resulting from third-party actions or project failures</li>
              <li>Damages arising from regulatory actions or changes</li>
            </ul>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              Our total liability shall not exceed the amount of fees paid by you to Onicorn in the twelve (12) months
              preceding the claim.
            </p>
          </section>

          {/* Section 14 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">14. Indemnification</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              You agree to indemnify, defend, and hold harmless Onicorn and its affiliates, officers, directors,
              employees, and agents from and against any claims, liabilities, damages, losses, and expenses
              (including reasonable attorney's fees) arising out of or in any way connected with:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Your use of the Platform</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your investment decisions and their outcomes</li>
            </ul>
          </section>

          {/* Section 15 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">15. Force Majeure</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              Onicorn shall not be liable for any failure or delay in performing our obligations under these Terms
              due to circumstances beyond our reasonable control, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Natural disasters, acts of God, or extreme weather events</li>
              <li>War, terrorism, civil unrest, or government actions</li>
              <li>Cyberattacks, network failures, or infrastructure outages</li>
              <li>Blockchain network congestion, forks, or protocol changes</li>
              <li>Regulatory changes or legal requirements</li>
            </ul>
          </section>

          {/* Section 16 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">16. Termination</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              Onicorn reserves the right to suspend or terminate your access to the Platform at any time, with or
              without cause, and with or without notice. Reasons for termination may include, but are not limited to:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Violation of these Terms</li>
              <li>Suspected fraudulent or illegal activity</li>
              <li>Regulatory requirements or legal orders</li>
              <li>Extended periods of inactivity</li>
            </ul>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              Upon termination, your right to use the Platform will immediately cease. Provisions of these Terms
              that by their nature should survive termination shall survive.
            </p>
          </section>

          {/* Section 17 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">17. Governing Law and Dispute Resolution</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              These Terms shall be governed by and construed in accordance with applicable laws, without regard to
              conflict of law principles. Any disputes arising out of or relating to these Terms or your use of the
              Platform shall be resolved through binding arbitration, except where prohibited by law.
            </p>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              You agree to waive any right to participate in class action lawsuits or class-wide arbitration against Onicorn.
            </p>
          </section>

          {/* Section 18 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">18. Changes to Terms</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              Onicorn reserves the right to modify these Terms at any time. We will notify users of material changes
              through the Platform or via email. Your continued use of the Platform after such modifications constitutes
              your acceptance of the updated Terms.
            </p>
          </section>

          {/* Section 19 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">19. Contact Information</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              <strong className="text-white">Onicorn</strong><br />
              Email: legal@onicorn.io<br />
              Website: https://onicorn.io
            </p>
          </section>

          {/* Footer */}
          <div className="border-t border-[#282828] pt-8 mt-12">
            <p className="text-[#666] text-sm">
              By using the Onicorn platform, you acknowledge that you have read, understood, and agree to be bound
              by these Terms of Service.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/terms')({
  component: TermsPage,
});
